import { z } from 'zod';

import {
  PurchaseSettlementPolicySourceSchema,
  PurchaseSettlementReferenceCounterMethodSchema,
  PurchaseSettlementReferenceCounterScopeSchema,
  PurchaseSettlementRuntimeMethodSchema,
} from './purchases-settlement.schema.js';
import type {
  GetEffectivePurchaseSettlementPolicyRequest,
  GetEffectivePurchaseSettlementPolicyResponse,
  GetPurchaseSettlementCompanyPolicyRequest,
  GetPurchaseSettlementCompanyPolicyResponse,
  ListPurchaseSettlementPoliciesRequest,
  ListPurchaseSettlementPoliciesResponse,
  PurchaseSettlementPolicyAdminAutoReferenceConfig,
  PurchaseSettlementPolicyAdminCounterConfig,
  PurchaseSettlementPolicyAdminManualReferenceAllowance,
  PurchaseSettlementPolicyAdminPolicy,
  PurchaseSettlementPolicyAdminReferenceCounter,
  PurchaseSettlementPolicyAdminSettings,
  UpdatePurchaseSettlementCompanyPolicyRequest,
  UpdatePurchaseSettlementCompanyPolicyResponse,
} from './purchases-settlement-policy-admin.dto.js';

const timestampSchema = z.iso.datetime();
const counterPrefixSchema = z.string().trim().min(1).max(24).nullable();
const voucherRedeemableAtSchema = z.string().trim().min(1).max(200).nullable();

const adminSettingsShape = {
  enabledMethods: z.array(PurchaseSettlementRuntimeMethodSchema).min(1),
  defaultMethod: PurchaseSettlementRuntimeMethodSchema,
  autoReference: z
    .object({
      check: z
        .object({
          enabled: z.boolean(),
          prefix: counterPrefixSchema,
          padding: z.number().int().min(1).max(12),
          nextNumber: z.number().int().positive(),
        })
        .strict(),
      voucher: z
        .object({
          enabled: z.boolean(),
          prefix: counterPrefixSchema,
          padding: z.number().int().min(1).max(12),
          nextNumber: z.number().int().positive(),
        })
        .strict(),
    })
    .strict(),
  manualReferenceAllowance: z
    .object({
      check: z.boolean(),
      voucher: z.boolean(),
    })
    .strict(),
  voucherRedeemableAt: voucherRedeemableAtSchema,
} as const;

export const PurchaseSettlementPolicyAdminMethodSchema = PurchaseSettlementRuntimeMethodSchema;
export const PurchaseSettlementPolicyAdminScopeSchema = PurchaseSettlementReferenceCounterScopeSchema;

export const PurchaseSettlementPolicyAdminCounterConfigSchema: z.ZodType<PurchaseSettlementPolicyAdminCounterConfig> =
  adminSettingsShape.autoReference.shape.check;

export const PurchaseSettlementPolicyAdminAutoReferenceConfigSchema: z.ZodType<PurchaseSettlementPolicyAdminAutoReferenceConfig> =
  adminSettingsShape.autoReference;

export const PurchaseSettlementPolicyAdminManualReferenceAllowanceSchema: z.ZodType<PurchaseSettlementPolicyAdminManualReferenceAllowance> =
  adminSettingsShape.manualReferenceAllowance;

export const PurchaseSettlementPolicyAdminSettingsSchema: z.ZodType<PurchaseSettlementPolicyAdminSettings> =
  z.object(adminSettingsShape).strict().superRefine(validateAdminSettings);

export const PurchaseSettlementPolicyAdminPolicySchema: z.ZodType<PurchaseSettlementPolicyAdminPolicy> =
  z
    .object({
      ...adminSettingsShape,
      id: z.uuid().nullable(),
      companyId: z.uuid(),
      scope: PurchaseSettlementReferenceCounterScopeSchema,
      scopeId: z.uuid(),
      locationId: z.uuid().nullable().optional(),
      stationId: z.uuid().nullable().optional(),
      source: PurchaseSettlementPolicySourceSchema,
      editable: z.boolean(),
      createdAt: timestampSchema.nullable().optional(),
      updatedAt: timestampSchema.nullable().optional(),
    })
    .strict()
    .superRefine((value, ctx) => {
      validateAdminSettings(value, ctx);

      if (value.scope !== 'company' && value.editable) {
        ctx.addIssue({
          code: 'custom',
          message: 'Only company-level settlement policies are editable in v1 admin contracts.',
          path: ['editable'],
        });
      }

      if (value.scope === 'location' && !value.locationId) {
        ctx.addIssue({
          code: 'custom',
          message: 'locationId is required for location settlement policy rows.',
          path: ['locationId'],
        });
      }

      if (value.scope === 'station' && !value.stationId) {
        ctx.addIssue({
          code: 'custom',
          message: 'stationId is required for station settlement policy rows.',
          path: ['stationId'],
        });
      }
    });

export const PurchaseSettlementPolicyAdminReferenceCounterSchema: z.ZodType<PurchaseSettlementPolicyAdminReferenceCounter> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      method: PurchaseSettlementReferenceCounterMethodSchema,
      scope: PurchaseSettlementReferenceCounterScopeSchema,
      scopeId: z.uuid(),
      locationId: z.uuid().nullable().optional(),
      stationId: z.uuid().nullable().optional(),
      enabled: z.boolean(),
      prefix: counterPrefixSchema,
      padding: z.number().int().min(1).max(12),
      nextNumber: z.number().int().positive(),
      createdAt: timestampSchema,
      updatedAt: timestampSchema,
    })
    .strict()
    .refine((value) => (value.scope === 'location' ? Boolean(value.locationId) : true), {
      message: 'locationId is required for location reference counters.',
      path: ['locationId'],
    })
    .refine((value) => (value.scope === 'station' ? Boolean(value.stationId) : true), {
      message: 'stationId is required for station reference counters.',
      path: ['stationId'],
    });

export const ListPurchaseSettlementPoliciesRequestSchema: z.ZodType<ListPurchaseSettlementPoliciesRequest> =
  z.object({
    companyId: z.uuid(),
  });

export const ListPurchaseSettlementPoliciesResponseSchema: z.ZodType<ListPurchaseSettlementPoliciesResponse> =
  z.object({
    items: z.array(PurchaseSettlementPolicyAdminPolicySchema),
    referenceCounters: z.array(PurchaseSettlementPolicyAdminReferenceCounterSchema),
  });

export const GetPurchaseSettlementCompanyPolicyRequestSchema: z.ZodType<GetPurchaseSettlementCompanyPolicyRequest> =
  z.object({
    companyId: z.uuid(),
  });

export const GetPurchaseSettlementCompanyPolicyResponseSchema: z.ZodType<GetPurchaseSettlementCompanyPolicyResponse> =
  z.object({
    policy: PurchaseSettlementPolicyAdminPolicySchema,
  });

export const UpdatePurchaseSettlementCompanyPolicyRequestSchema: z.ZodType<UpdatePurchaseSettlementCompanyPolicyRequest> =
  z
    .object({
      companyId: z.uuid(),
      ...adminSettingsShape,
    })
    .strict()
    .superRefine(validateAdminSettings);

export const UpdatePurchaseSettlementCompanyPolicyResponseSchema: z.ZodType<UpdatePurchaseSettlementCompanyPolicyResponse> =
  z.object({
    policy: PurchaseSettlementPolicyAdminPolicySchema,
  });

export const GetEffectivePurchaseSettlementPolicyRequestSchema: z.ZodType<GetEffectivePurchaseSettlementPolicyRequest> =
  z.object({
    companyId: z.uuid(),
    locationId: z.uuid().nullable().optional(),
    stationId: z.uuid().nullable().optional(),
  });

export const GetEffectivePurchaseSettlementPolicyResponseSchema: z.ZodType<GetEffectivePurchaseSettlementPolicyResponse> =
  z.object({
    policy: PurchaseSettlementPolicyAdminPolicySchema,
    referenceCounters: z.array(PurchaseSettlementPolicyAdminReferenceCounterSchema),
  });

function validateAdminSettings(
  value: PurchaseSettlementPolicyAdminSettings,
  ctx: z.RefinementCtx,
): void {
  const enabledMethods = new Set(value.enabledMethods);

  if (enabledMethods.size !== value.enabledMethods.length) {
    ctx.addIssue({
      code: 'custom',
      message: 'Enabled settlement methods must be unique.',
      path: ['enabledMethods'],
    });
  }

  if (!enabledMethods.has('cash')) {
    ctx.addIssue({
      code: 'custom',
      message: 'Cash settlement must remain enabled.',
      path: ['enabledMethods'],
    });
  }

  if (!enabledMethods.has(value.defaultMethod)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Default settlement method must be enabled.',
      path: ['defaultMethod'],
    });
  }

  validateReferencePaths(value, 'check', ctx);
  validateReferencePaths(value, 'voucher', ctx);

  if (value.defaultMethod === 'check' && !value.autoReference.check.enabled) {
    ctx.addIssue({
      code: 'custom',
      message: 'Check default settlement requires auto-reference.',
      path: ['autoReference', 'check', 'enabled'],
    });
  }

  if (value.defaultMethod === 'voucher' && !value.autoReference.voucher.enabled) {
    ctx.addIssue({
      code: 'custom',
      message: 'Voucher default settlement requires auto-reference.',
      path: ['autoReference', 'voucher', 'enabled'],
    });
  }
}

function validateReferencePaths(
  value: PurchaseSettlementPolicyAdminSettings,
  method: 'check' | 'voucher',
  ctx: z.RefinementCtx,
): void {
  if (!value.enabledMethods.includes(method)) {
    return;
  }

  const hasAutoReference = value.autoReference[method].enabled;
  const hasManualReference = value.manualReferenceAllowance[method];
  if (!hasAutoReference && !hasManualReference) {
    ctx.addIssue({
      code: 'custom',
      message: `${method} settlement requires auto-reference or manual reference allowance.`,
      path: ['autoReference', method, 'enabled'],
    });
  }

  if (method === 'voucher' && !value.voucherRedeemableAt?.trim()) {
    ctx.addIssue({
      code: 'custom',
      message: 'Voucher settlement requires redeemable-at display text.',
      path: ['voucherRedeemableAt'],
    });
  }
}
